import React, { ChangeEvent, useEffect, useState } from 'react';
import { getService } from '@Client/runner.hooks/useService';
import {
  TreeView,
  TreeViewItemClickEvent
} from '@progress/kendo-react-treeview';
import ICategoryDetail from '@Shared.Angular/@types/core/contracts/queryModel/category/categoryDetail';
import { Guid } from '@Shared.Angular/@types/guid';
import Modal from '@Shared.Angular/flowingly.components/Modal/Modal';
import {
  CustomTabs,
  ICustomTab
} from '@Shared.Angular/flowingly.components/CustomTabs/CustomTabs';
import { CustomTabPanel } from '@Shared.Angular/flowingly.components/CustomTabs/CustomTab/CustomTabPanel';
import { FlowinglyPermissions } from '@Shared.Angular/flowingly.services/flowingly.constants';
import { SmartSelect } from '@Shared.Angular/flowingly.components/smartselect/SmartSelect/SmartSelect';
import IFlowinglySearchResult from '@Shared.Angular/@types/core/contracts/queryModel/search/flowinglySearchResult';
import { InfoIcon } from '@Shared.Angular/flowingly.components/InfoIcon/InfoIcon';
import { SelectUser } from '@Shared.Angular/flowingly.components/SelectUser/SelectUser';

export type Props = {
  categoryId: Guid;
  categoryName: string;
  description: string;
  approverId?: Guid;
  parentId: Guid;
  flowModelEditors?: IFlowinglySearchResult[];
  closeModal: () => void;
  onCategoryChange: () => Promise<void>;
};

type TreeViewItem = {
  id: Guid;
  text: string;
  expanded: boolean;
  selected: boolean;
  items: TreeViewItem[];
};

enum CategoryEditTabId {
  DETAILS = 'details',
  GOVERNANCE = 'governance'
}

const CategoryAddEditModal = (props: Props) => {
  const {
    categoryName,
    description,
    approverId,
    flowModelEditors,
    closeModal,
    onCategoryChange
  } = props;

  const [categoryNameInput, setCategoryNameInput] = useState(categoryName);
  const [descriptionInput, setDescriptionInput] = useState(description);
  const [approverIdInput, setApproverIdInput] = useState(approverId);
  const [updatedFlowModelEditors, setUpdatedFlowModelEditors] = useState<
    IFlowinglySearchResult[]
  >([]);
  const [categories, setCategories] = useState<TreeViewItem[]>([]);
  const [parent, setParent] = useState<ICategoryDetail>({
    id: props.parentId,
    name: !props.parentId ? 'Root' : ''
  });
  const [selectedTabId, setSelectedTabId] = useState<CategoryEditTabId>(
    CategoryEditTabId.DETAILS
  );

  const permissionsService = getService('permissionsService');
  const notificationService = getService('notificationService');
  const appConfig = getService('APP_CONFIG');

  const categoryApproverPermissions = [
    FlowinglyPermissions.GOVERNANCE_FLOWMODEL_PUBLISHER_CANDIDATE
  ];

  const categoryEditorPermissions = [
    FlowinglyPermissions.GOVERNANCE_FLOWMODEL_EDITOR_CANDIDATE
  ];

  useEffect(() => {
    getService('categoryApiService')
      .getCategories()
      .then((results) => {
        const treeViewCategories = results
          .map((category) =>
            mapCategoryToTreeItem(category, props.categoryId, props.parentId)
          )
          .filter((category) => category !== null);

        setCategories([
          {
            id: null,
            text: 'Root',
            expanded: true,
            selected: !props.parentId,
            items: treeViewCategories
          }
        ]);
      });
  }, []);

  const mapCategoryToTreeItem = (
    category: ICategoryDetail,
    targetCategoryId: Guid,
    targetParentId: Guid
  ) => {
    if (category.id === targetParentId) {
      setParent({
        id: category.id,
        name: category.name
      });
    }
    if (category.id === targetCategoryId) {
      return null;
    }
    const treeItem: TreeViewItem = {
      id: category.id,
      text: category.name,
      items: category.subCategories
        .map((child) =>
          mapCategoryToTreeItem(child, targetCategoryId, targetParentId)
        )
        .filter((child) => child !== null),
      selected: category.id === targetParentId,
      expanded: false
    };
    treeItem.expanded = treeItem.items.some(
      (item) => item.selected || item.expanded
    );
    return treeItem;
  };

  const setParentCategory = (event: TreeViewItemClickEvent) => {
    setParent({
      id: event.item.id,
      name: event.item.text
    });
  };

  const save = async () => {
    if (!categoryNameInput) {
      return;
    }
    const category: ICategoryDetail = {
      id: props.categoryId,
      name: categoryNameInput,
      description: descriptionInput,
      approverId: approverIdInput,
      flowModelEditors: updatedFlowModelEditors,
      parentId: parent.id
    };
    try {
      if (!props.categoryId) {
        const response = await getService('categoryApiService').addCategory(
          category
        );
        if (response.data.success) {
          closeModal();
          notificationService.showSuccessToast('Category created');
          getService('pubsubService').publish(
            'CATEGORY_WITH_SUBCATEGORY_ADDED'
          );
        } else {
          notificationService.showErrorToast(response.data.errorMessage);
        }
      } else {
        getService('appInsightsService').startEventTimer('categoryEdited');
        const response = await getService('categoryApiService').editCategory(
          category
        );
        if (response.data.success) {
          closeModal();
          notificationService.showSuccessToast('Category updated successfully');
          await onCategoryChange();
        } else {
          notificationService.showErrorToast(response.data.errorMessage);
        }
      }
    } catch (err) {
      console.error(err);
      notificationService.showErrorToast('Error while updating Category');
    }
  };

  const handleCategoryNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    setCategoryNameInput(event.target.value);
  };

  const handleDescriptionChange = (event: ChangeEvent<HTMLInputElement>) => {
    setDescriptionInput(event.target.value);
  };

  const handleApproverChange = (userId?: Guid) => {
    setApproverIdInput(userId);
  };

  const tabs: ICustomTab[] = [
    { id: CategoryEditTabId.DETAILS, label: 'Details' },
    {
      id: CategoryEditTabId.GOVERNANCE,
      label: 'Governance',
      isHidden:
        !appConfig.enableGovernance ||
        !permissionsService.currentUserHasPermission(
          FlowinglyPermissions.GOVERNANCE_CATEGORY_EDIT
        )
    }
  ];

  const visibleTabs = tabs.filter((tab) => !tab.isHidden);
  const title = !props.categoryId
    ? 'Add Category'
    : props.parentId
    ? 'Edit Sub Category'
    : 'Edit Category';
  const editorSelectTitle = appConfig.enableProcedures
    ? 'Flow Model and SOP Editors'
    : 'Flow Model Editors';
  const editorSelectMessage = appConfig.enableProcedures
    ? 'Actors who can edit flow models and SOPs in this category'
    : 'Actors who can edit flow models in this category';

  return (
    <Modal
      id="category-edit-modal"
      title={title}
      actionButtonText="Save"
      actionButtonType="primary"
      onCancelClick={closeModal}
      onClose={closeModal}
      onActionClick={save}
      size="md"
    >
      <div>
        <CustomTabs
          selectedTabId={selectedTabId}
          onChange={(newTabId) =>
            setSelectedTabId(newTabId as CategoryEditTabId)
          }
          tabItems={visibleTabs}
        />
        <CustomTabPanel
          tabId={CategoryEditTabId.DETAILS}
          isShown={selectedTabId === CategoryEditTabId.DETAILS}
        >
          <div className="input-field">
            <label htmlFor="category-name">Category Name *</label>
            <input
              id="category-name"
              type="text"
              value={categoryNameInput}
              onChange={handleCategoryNameChange}
              required
            />
            {!categoryNameInput && (
              <div className="error-msg">Please enter a Category name</div>
            )}
          </div>
          <div className="input-field">
            <label htmlFor="category-description">
              Description <span className="optional"></span>
            </label>
            <input
              id="category-description"
              type="text"
              value={descriptionInput}
              onChange={handleDescriptionChange}
            />
          </div>

          {appConfig.maxSubCategories > 1 && (
            <div>
              <div className="input-field">
                <label>
                  Parent Category
                  <InfoIcon message="The Parent Category will contain this Category" />
                </label>
                <input
                  id="category-parent"
                  value={parent.name}
                  type="text"
                  disabled={true}
                />
              </div>
              <TreeView
                data={categories}
                expandIcons={true}
                onExpandChange={function (event) {
                  event.item.expanded = !event.item.expanded;
                  setParentCategory(event);
                }}
                onItemClick={setParentCategory}
              />
            </div>
          )}
        </CustomTabPanel>
        <CustomTabPanel
          tabId={CategoryEditTabId.GOVERNANCE}
          isShown={selectedTabId === CategoryEditTabId.GOVERNANCE}
        >
          <div className="input-field">
            <label htmlFor="category-approver">
              Category Publisher
              <InfoIcon message="Users must have Flow Model Category Publisher permission to appear in this list" />
              <span className="optional"></span>
            </label>
            <div className="select-approver-container">
              <span id="category-approver" className="select-approver">
                <SelectUser
                  permissionSecureCodes={categoryApproverPermissions}
                  selectedUserId={approverIdInput}
                  onUserSelect={handleApproverChange}
                  popupClassName="edit-category-dropdown-tree-popup"
                />
              </span>
            </div>
          </div>
          <div className="input-field">
            <label htmlFor="category-editors">
              Category Editors
              <InfoIcon message="Users must have Flow Model Category Editor permission to appear in this list" />
              <span className="optional"></span>
            </label>
            <div>
              <span id="category-editors">
                <SmartSelect
                  initialSelectedItems={flowModelEditors}
                  headerText={editorSelectTitle}
                  infoText={editorSelectMessage}
                  userListAllowedPermissions={categoryEditorPermissions}
                  onChange={(selected) => setUpdatedFlowModelEditors(selected)}
                ></SmartSelect>
              </span>
            </div>
          </div>
        </CustomTabPanel>
      </div>
    </Modal>
  );
};

export default CategoryAddEditModal;
